import { useState } from "react";
import {
  ShieldCheck,
  FileText,
  Lock,
  CheckCircle2,
  Hash,
  Download,
  AlertTriangle,
  ScrollText,
  Layers,
} from "lucide-react";
import {
  useScorecards,
  useBoardPacks,
  useCreateBoardPack,
  useSignBoardPack,
  useAudit,
} from "../../lib/queries";
import {
  GlassCard,
  StatCard,
  SectionTag,
  Pill,
  Button,
  Spinner,
  EmptyState,
} from "../../components/ui";
import { fmtAUD, fmtNum, fmtTime, timeAgo } from "../../lib/format";
import type { BoardPack, Scorecard } from "../../types/api";

const TRUST_CHIPS = ["TLS 1.3", "AES-256", "GRC PUSH", "ANONYMIZED AT EXEC LEVEL"];

const COHORTS = ["Highest-risk fleets", "Mixed surface", "Underground", "All fleets"];

const PACK_COLOR: Record<BoardPack["status"], string> = {
  drafting: "#F59E0B",
  drafted: "#22D3EE",
  signed: "#2DD4BF",
};

function todayLabel(): string {
  return new Date().toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Comply() {
  const { data: scorecards, isLoading: scLoading } = useScorecards();
  const { data: packs, isLoading: packsLoading } = useBoardPacks();
  const { data: audit, isLoading: auditLoading } = useAudit();
  const createPack = useCreateBoardPack();
  const signPack = useSignBoardPack();

  const [cohort, setCohort] = useState(0);

  const org: Scorecard | undefined = (scorecards ?? []).find(
    (s) => s.tenant === null || s.anonymized
  );
  const fleets: Scorecard[] = (scorecards ?? [])
    .filter((s) => s.tenant !== null && !s.anonymized)
    .sort((a, b) => b.critical_events - a.critical_events);

  const onGenerate = () => {
    if (!org) return;
    createPack.mutate({
      title: `Board Safety Pack — ${todayLabel()}`,
      scorecard: org.id,
    });
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <SectionTag>COMPLY · GOVERNANCE &amp; ASSURANCE</SectionTag>
          <h1 className="text-xl font-semibold">Cross-Fleet Safety Scorecard</h1>
        </div>
        <span className="flex items-center gap-1.5 text-[11px] text-slatey">
          <ShieldCheck size={14} className="text-aurora-teal" />
          Assurance-grade · privacy-preserving
        </span>
      </div>

      {/* Hero anonymized scorecard */}
      {scLoading ? (
        <GlassCard>
          <Spinner label="Compiling cross-fleet scorecard…" />
        </GlassCard>
      ) : !org ? (
        <EmptyState>No org-wide scorecard available.</EmptyState>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
            <StatCard
              label="SAFETY SCORE"
              value={`${org.safety_score}%`}
              accent="var(--pacific)"
            />
            <StatCard label="LTIR" value={org.ltir.toFixed(2)} accent="var(--aurora-cyan)" sub="per 200k hrs" />
            <StatCard label="ESG" value={`${org.esg_score}/100`} accent="var(--aurora-teal)" />
            <StatCard label="RISK AVERTED" value={fmtAUD(org.risk_averted_aud)} accent="var(--aurora-teal)" />
            <StatCard label="EXCEPTIONS" value={fmtNum(org.total_events)} accent="var(--accent)" />
            <StatCard label="CRITICAL" value={fmtNum(org.critical_events)} critical sub="period" />
            <StatCard label="FP-RATE" value={`${(org.fp_rate * 100).toFixed(1)}%`} accent="var(--aurora-cyan)" />
          </div>

          {/* Trust chips */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 text-[11px] text-slatey">
              <Lock size={12} className="text-aurora-teal" />
              Org-wide · anonymized
            </span>
            {TRUST_CHIPS.map((c) => (
              <Pill key={c} color="#2DD4BF">
                {c}
              </Pill>
            ))}
          </div>
        </div>
      )}

      {/* Cohort builder / per-fleet table */}
      <GlassCard>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <SectionTag>COHORT BUILDER · PER-FLEET SCORECARDS</SectionTag>
          <div className="flex flex-wrap gap-1.5">
            {COHORTS.map((c, i) => (
              <button
                key={c}
                onClick={() => setCohort(i)}
                className={`rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wider transition ${
                  i === cohort
                    ? "border border-aurora-cyan/40 bg-aurora-cyan/10 text-aurora-cyan"
                    : "border border-white/10 bg-white/5 text-slatey hover:text-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {scLoading ? (
          <Spinner />
        ) : fleets.length === 0 ? (
          <EmptyState>No per-fleet scorecards in cohort.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="section-tag border-b border-white/10 text-slatey">
                  <th className="py-2 pr-3 font-normal">Fleet</th>
                  <th className="py-2 pr-3 text-right font-normal">Exceptions</th>
                  <th className="py-2 pr-3 text-right font-normal">Critical</th>
                  <th className="py-2 pr-3 text-right font-normal">Safety %</th>
                  <th className="py-2 pr-3 text-right font-normal">Risk averted</th>
                  <th className="py-2 pr-3 text-right font-normal">LTIR</th>
                </tr>
              </thead>
              <tbody>
                {fleets.map((f) => (
                  <tr
                    key={f.id}
                    className="border-b border-white/5 transition hover:bg-white/[0.03]"
                  >
                    <td className="py-2 pr-3">
                      <span className="flex items-center gap-1.5">
                        <Layers size={12} className="text-slatey" />
                        <span className="font-medium">{f.tenant_name}</span>
                      </span>
                    </td>
                    <td className="mono py-2 pr-3 text-right">{fmtNum(f.total_events)}</td>
                    <td
                      className="mono py-2 pr-3 text-right"
                      style={{ color: f.critical_events > 0 ? "var(--critical)" : undefined }}
                    >
                      {fmtNum(f.critical_events)}
                    </td>
                    <td className="mono py-2 pr-3 text-right text-pacific">{f.safety_score}%</td>
                    <td className="mono py-2 pr-3 text-right text-aurora-teal">
                      {fmtAUD(f.risk_averted_aud)}
                    </td>
                    <td className="mono py-2 pr-3 text-right">{f.ltir.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2 text-[11px] text-slatey">Cohort: highest-risk fleets · 30d</div>
      </GlassCard>

      <div className="grid gap-3 lg:grid-cols-2">
        {/* Board pack export */}
        <GlassCard>
          <div className="mb-3 flex items-center justify-between">
            <SectionTag>BOARD PACK EXPORT</SectionTag>
            <Button variant="cyan" onClick={onGenerate} disabled={!org || createPack.isPending}>
              <span className="flex items-center gap-1.5">
                <FileText size={14} />
                {createPack.isPending ? "Generating…" : "Generate board pack"}
              </span>
            </Button>
          </div>

          {packsLoading ? (
            <Spinner />
          ) : (packs ?? []).length === 0 ? (
            <EmptyState>No board packs yet — generate one to brief the board.</EmptyState>
          ) : (
            <div className="space-y-2">
              {(packs ?? []).map((p) => (
                <div
                  key={p.id}
                  className="rounded-lg border border-white/5 bg-white/[0.03] p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{p.title}</div>
                      <div className="mt-0.5 text-[11px] text-slatey">
                        {p.generated_at
                          ? `generated ${timeAgo(p.generated_at)}`
                          : `queued ${timeAgo(p.created_at)}`}
                        {p.signed_by_name ? ` · signed by ${p.signed_by_name}` : ""}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Pill color={PACK_COLOR[p.status]}>
                        {p.status === "drafting" ? (
                          <span className="flex items-center gap-1">
                            <span className="h-1.5 w-1.5 animate-pulseDot rounded-full bg-current" />
                            drafting
                          </span>
                        ) : (
                          p.status
                        )}
                      </Pill>
                      {p.status === "signed" && (
                        <CheckCircle2 size={15} className="text-aurora-teal" />
                      )}
                    </div>
                  </div>

                  {p.summary && (
                    <p className="mt-2 text-xs text-slatey">{p.summary}</p>
                  )}

                  {p.status === "drafted" && (
                    <div className="mt-2 flex justify-end">
                      <Button
                        variant="ghost"
                        onClick={() => signPack.mutate(p.id)}
                        disabled={signPack.isPending}
                      >
                        <span className="flex items-center gap-1.5">
                          <Download size={13} />
                          Sign &amp; release
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slatey">
            <ShieldCheck size={12} className="text-aurora-teal" />
            AI-drafted, human-signed.
          </div>
        </GlassCard>

        {/* Immutable audit trail */}
        <GlassCard>
          <div className="mb-3 flex items-center justify-between">
            <SectionTag>IMMUTABLE AUDIT TRAIL</SectionTag>
            {audit &&
              (audit.integrity.ok ? (
                <Pill color="#2DD4BF">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 size={12} />
                    HASH-CHAIN VERIFIED · {audit.integrity.entries ?? audit.entries.length} ENTRIES
                  </span>
                </Pill>
              ) : (
                <Pill color="#EF4444">
                  <span className="flex items-center gap-1">
                    <AlertTriangle size={12} />
                    CHAIN BROKEN AT #{audit.integrity.broken_at}
                  </span>
                </Pill>
              ))}
          </div>

          {auditLoading ? (
            <Spinner />
          ) : !audit || audit.entries.length === 0 ? (
            <EmptyState>
              <span className="flex items-center gap-1.5">
                <ScrollText size={14} /> No audit events recorded.
              </span>
            </EmptyState>
          ) : (
            <div className="max-h-[360px] overflow-auto pr-1">
              <table className="w-full min-w-[560px] text-left text-xs">
                <thead className="sticky top-0 bg-[#0D1F33]">
                  <tr className="section-tag border-b border-white/10 text-slatey">
                    <th className="py-2 pr-3 font-normal">When</th>
                    <th className="py-2 pr-3 font-normal">Action</th>
                    <th className="py-2 pr-3 font-normal">Actor</th>
                    <th className="py-2 pr-3 font-normal">Object</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.entries.map((e, i) => (
                    <tr
                      key={e.id}
                      className="border-b border-white/5 align-top transition hover:bg-white/[0.03]"
                    >
                      <td className="py-2 pr-3" title={fmtTime(e.created_at)}>
                        <span className="mono text-slatey">{timeAgo(e.created_at)}</span>
                      </td>
                      <td className="py-2 pr-3 font-medium">{e.action}</td>
                      <td className="py-2 pr-3 text-slatey">{e.actor}</td>
                      <td className="py-2 pr-3">
                        <div className="text-slatey">
                          {e.object_type}
                          <span className="mono"> #{e.object_id}</span>
                        </div>
                        {i < 3 && e.hash && (
                          <div className="mono mt-0.5 flex items-center gap-1 text-[10px] text-aurora-cyan/70">
                            <Hash size={10} />
                            {e.hash.slice(0, 10)}…
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
